import { useNavigate } from 'react-router-dom';
import { ModuleHeader } from '@/components/ui/module-header';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import * as Icons from 'lucide-react';

const SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: 'Rocket',
    content: [
      'Create your first load from the Loads page',
      'Add parties (shippers/consignees) to speed up load creation',
      'Set up your fleet and drivers before dispatching',
    ],
  },
  {
    id: 'trips-tracking',
    title: 'Trips & Tracking',
    icon: 'Route',
    content: [
      'Generate customer portal tokens to share tracking links',
      'Update trip status to reflect real-world progress',
      'Log expenses per trip for accurate settlement calculations',
    ],
  },
  {
    id: 'documents',
    title: 'Document Packets',
    icon: 'FolderOpen',
    content: [
      'Upload BOL, POD, rate confirmation, fuel receipts per load',
      'POD required before settlement (can override with permission)',
    ],
  },
  {
    id: 'accounting',
    title: 'Accounting Logic',
    icon: 'Calculator',
    content: [
      'Settlements calculate: gross - deductions = net pay',
      'Deductions include: fuel advances, insurance, equipment',
      'AR aging buckets: current, 1-30, 31-60, 61-90, 90+ days',
      'Sync to QuickBooks Online for GL integration',
    ],
  },
  {
    id: 'saas-rules',
    title: 'Multi-Company SaaS Rules',
    icon: 'Building2',
    content: [
      'Each company workspace is fully isolated',
      'Starter plan includes 5 seats; upgrade for more',
    ],
  },
];

export default function GuidesPage() {
  const navigate = useNavigate();
  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Guides"
          description="Documentation and best practices for LogisticSlash"
          icon="Library"
          actions={
            <Button variant="outline" onClick={() => navigate('/docs')}>
              <Icons.FolderOpen className="h-4 w-4 mr-2" />
              Open Documents
            </Button>
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
          {/* TOC */}
          <aside className="lg:sticky lg:top-0 h-fit">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  Contents
                </p>
                <nav className="space-y-1">
                  {SECTIONS.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="block px-2 py-1.5 text-sm rounded-md hover:bg-accent transition-colors"
                    >
                      {s.title}
                    </a>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </aside>

          {/* Content */}
          <div className="space-y-6">
            {SECTIONS.map((s) => {
              const Icon = Icons[s.icon] || Icons.FileText;
              return (
                <Card key={s.id} id={s.id}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <h2 className="text-xl font-bold">{s.title}</h2>
                    </div>
                    <ul className="space-y-2 pl-2">
                      {s.content.map((line, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                          <Icons.Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
);
}
